import {
    alpha,
    Box,
    Card,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';
import { countryCodeToFlagEmoji } from '../dashboardAnalytics.utils';

type CountryRow = {
    countryCode: string;
    countryName: string;
    total: number;
    guests: number;
    users: number;
    subscribers: number;
};

type Props = {
    profileVisitorsLoadedCount: number;
    maxCount: number;
    profileVisitorsCountriesSummary: CountryRow[];
    profileVisitorsCountriesTotal: number;
    analyticsTableSurfaceHeaderBg: string;
};

const ProfileVisitorsCountriesCard = React.memo((props: Props) => {
    const {
        profileVisitorsLoadedCount,
        maxCount,
        profileVisitorsCountriesSummary,
        profileVisitorsCountriesTotal,
        analyticsTableSurfaceHeaderBg,
    } = props;

    return (
        <Card sx={{ p: 0 }}>
            {profileVisitorsLoadedCount < maxCount ? (
                <Skeleton height={220} />
            ) : profileVisitorsCountriesSummary.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 1.5 }}>
                    No country visitor data available for selected subscribers.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width="100%" sx={{ backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}>
                                    Country
                                </TableCell>
                                <TableCell align="right" sx={{ backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}>
                                    Total
                                </TableCell>
                                <TableCell align="right" sx={{ backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}>
                                    Guests
                                </TableCell>
                                <TableCell align="right" sx={{ backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}>
                                    Users
                                </TableCell>
                                <TableCell align="right" sx={{ backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}>
                                    Subscribers
                                </TableCell>
                                <TableCell
                                    align="left"
                                    sx={{ minWidth: 92, backgroundColor: analyticsTableSurfaceHeaderBg, fontWeight: 600 }}
                                >
                                    Share
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {profileVisitorsCountriesSummary.map(row => (
                                <TableRow key={`${row.countryCode}-${row.countryName}`}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" component="span" sx={{ lineHeight: 1 }}>
                                                {countryCodeToFlagEmoji(row.countryCode)}
                                            </Typography>
                                            <Typography variant="body2" component="span">
                                                {row.countryName}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                                    <TableCell align="right">{row.guests.toLocaleString()}</TableCell>
                                    <TableCell align="right">{row.users.toLocaleString()}</TableCell>
                                    <TableCell align="right">{row.subscribers.toLocaleString()}</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 92 }}>
                                        <Box
                                            sx={{
                                                width: 92,
                                                height: 8,
                                                borderRadius: 999,
                                                bgcolor: t => alpha(t.palette.primary.main, 0.2),
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${
                                                        profileVisitorsCountriesTotal > 0
                                                            ? (row.total / profileVisitorsCountriesTotal) * 100
                                                            : 0
                                                    }%`,
                                                    height: '100%',
                                                    bgcolor: 'primary.main',
                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Card>
    );
});

ProfileVisitorsCountriesCard.displayName = 'ProfileVisitorsCountriesCard';
export default ProfileVisitorsCountriesCard;
